import React, { useState, useEffect } from 'react';
import { landlordApi, partnerService } from '../../services/api';
import PartnerJobQueue from './PartnerJobQueue';
import PartnerAdminCalendar from './PartnerAdminCalendar';

// ============================================
// PARTNER API CONFIG
// ============================================
const tradeOptions = [
    { id: 'gas_safety', label: 'Gas Safety Engineer' },
    { id: 'electrician', label: 'Electrician (EICR)' },
    { id: 'epc_assessor', label: 'EPC Assessor' },
    { id: 'fire_safety', label: 'Fire Safety Specialist' },
    { id: 'locksmith', label: 'Locksmith' },
    { id: 'handyman', label: 'General Maintenance / Handyman' },
    { id: 'cleaning', label: 'End of Tenancy Cleaning' },
    { id: 'inventory_clerk', label: 'Inventory Clerk' }
];

// ============================================
// BRANDING CONSTANTS
// ============================================
const BRAND = {
    emerald: '#059669',
    emeraldDark: '#047857',
    emeraldLight: '#d1fae5',
    name: 'Landlord Pro',
    sub: 'Partner Hub',
};

// ============================================
// COMPONENTS
// ============================================

function PartnerLogin({ onLogin, onForgotPassword }: { onLogin: (token: string, partner: any) => void; onForgotPassword: () => void }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await landlordApi.post('api/partners/login', { email, password });
            const data = res.data;
            if (data.token) {
                localStorage.setItem('tradePartnerToken', data.token);
                onLogin(data.token, data.partner);
            }
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Network error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: 400, margin: '0 auto', background: '#fff', padding: 40, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 8, textAlign: 'center' }}>Partner Login</h2>
            <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginBottom: 32 }}>Access your trade portal</p>

            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 12, marginBottom: 20, fontSize: 14, fontWeight: 600 }}>{error}</div>}

            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 20 }}>
                    <label style={labelStyle}>Email Address</label>
                    <input type="email" required value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="partner@firm.com" />
                </div>
                <div style={{ marginBottom: 20 }}>
                    <label style={labelStyle}>Password</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type={showPassword ? "text" : "password"}
                            required
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            style={inputStyle}
                            placeholder="••••••••"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            style={{
                                position: 'absolute',
                                right: 12,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: '#6b7280',
                                fontSize: 16,
                                padding: 4,
                            }}
                            title={showPassword ? 'Hide password' : 'Show password'}
                        >
                            <i className={`fas fa-eye${showPassword ? '' : '-slash'}`}></i>
                        </button>
                    </div>
                </div>

                <div style={{ marginBottom: 24, textAlign: 'right' }}>
                    <button
                        type="button"
                        onClick={onForgotPassword}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: BRAND.emerald,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            textDecoration: 'none',
                            padding: 0,
                        }}
                    >
                        Forgot password?
                    </button>
                </div>

                <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.7 : 1 }}>
                    {loading ? 'Authenticating...' : 'Secure Login'}
                </button>
            </form>
        </div>
    );
}

function PartnerRegistration({ onRegistered }: { onRegistered: (email: string) => void }) {
    const [form, setForm] = useState({ businessName: '', contactName: '', email: '', phone: '', password: '', tradeType: 'gas_safety', registrationNumber: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            await landlordApi.post('api/partners/register', form);
            setSuccess('Registration successful! Redirecting to login...');
            setTimeout(() => onRegistered(form.email), 2000);
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Registration error');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div style={{ maxWidth: 500, margin: '0 auto', background: '#fff', padding: 48, borderRadius: 24, textAlign: 'center', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.1)' }}>
                <div style={{ width: 80, height: 80, background: '#dcfce7', color: '#16a34a', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 40, margin: '0 auto 24px' }}>
                    <i className="fas fa-check"></i>
                </div>
                <h3 style={{ fontSize: 28, fontWeight: 800, color: '#111827', marginBottom: 12 }}>Application Sent</h3>
                <p style={{ color: '#4b5563', fontSize: 16 }}>{success}</p>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 650, margin: '0 auto', background: '#fff', padding: 40, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 8, textAlign: 'center' }}>Become a Verified Partner</h2>
            <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginBottom: 32 }}>Join the UK's premier compliance network</p>

            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 12, marginBottom: 20, fontSize: 14, fontWeight: 600 }}>{error}</div>}

            <form onSubmit={handleSubmit} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Business Legal Name</label>
                    <input type="text" required value={form.businessName} onChange={e => setForm({ ...form, businessName: e.target.value })} style={inputStyle} placeholder="e.g. Acme Safety Services Ltd" />
                </div>
                <div>
                    <label style={labelStyle}>Contact Person</label>
                    <input type="text" required value={form.contactName} onChange={e => setForm({ ...form, contactName: e.target.value })} style={inputStyle} placeholder="John Doe" />
                </div>
                <div>
                    <label style={labelStyle}>Direct Phone</label>
                    <input type="tel" required value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputStyle} placeholder="07123 456789" />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Professional Email</label>
                    <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} placeholder="partners@firm.com" />
                </div>
                <div>
                    <label style={labelStyle}>Define Password</label>
                    <input type="password" required value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} style={inputStyle} placeholder="••••••••" />
                </div>
                <div>
                    <label style={labelStyle}>Core Speciality</label>
                    <select required value={form.tradeType} onChange={e => setForm({ ...form, tradeType: e.target.value })} style={inputStyle}>
                        {tradeOptions.map(opt => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                    </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                    <label style={labelStyle}>Gas Safe / NICEIC / Accreditation NO.</label>
                    <input type="text" value={form.registrationNumber} onChange={e => setForm({ ...form, registrationNumber: e.target.value })} style={inputStyle} placeholder="Verification ID" />
                </div>

                <div style={{ gridColumn: '1 / -1', marginTop: 12 }}>
                    <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.7 : 1 }}>
                        {loading ? 'Processing Application...' : 'Apply for Verification'}
                    </button>
                    <p style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 16 }}>
                        By clicking apply, you authorize Compliance Engine to verify your trade credentials.
                    </p>
                </div>
            </form>
        </div>
    );
}

// ============================================
// ONBOARDING FORM (Post-Approval)
// ============================================
function OnboardingForm({ partner, onComplete }: { partner: any; onComplete: (updated: any) => void }) {
    const [form, setForm] = useState({
        trade: 'Gas Engineer',
        areaCovered: 'Manchester City',
        competenceRegistration: 'Gas Safety',
        registrationNumber: '503346',
        insuranceCertificate: '',
        publicLiability: '',
        professionalIndemnity: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await partnerService.submitOnboarding(form);
            onComplete(res.data.partner);
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Submission failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex-1 flex flex-col justify-center items-center p-8">
            <div className="max-w-3xl w-full bg-white p-10 md:p-14 rounded-[32px] shadow-2xl shadow-gray-200/50 border border-gray-50">
                {/* Progress header */}
                <div className="flex items-center gap-4 mb-10">
                    <div className="w-12 h-12 bg-emerald-100 text-emerald-700 rounded-2xl flex items-center justify-center text-xl">
                        <i className="fas fa-clipboard-check"></i>
                    </div>
                    <div>
                        <h2 className="text-2xl font-black text-gray-900 tracking-tight">Complete Your Onboarding</h2>
                        <p className="text-gray-500 text-sm font-medium">Your application has been approved. Please provide the details below to start receiving jobs.</p>
                    </div>
                </div>

                {error && <div className="bg-red-50 text-red-800 px-5 py-3 rounded-xl mb-6 text-sm font-bold border border-red-100">{error}</div>}

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label style={labelStyle}>Your Trade</label>
                            <select value={form.trade} onChange={e => setForm({ ...form, trade: e.target.value })} style={inputStyle} required>
                                <option value="Gas Engineer">Gas Engineer</option>
                                <option value="Electrician">Electrician</option>
                                <option value="EPC Assessor">EPC Assessor</option>
                                <option value="Locksmith">Locksmith</option>
                                <option value="Fire Safety Specialist">Fire Safety Specialist</option>
                                <option value="Handyman">General Handyman</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Area Covered</label>
                            <input type="text" value={form.areaCovered} onChange={e => setForm({ ...form, areaCovered: e.target.value })} style={inputStyle} placeholder="e.g. Manchester City" required />
                        </div>
                        <div>
                            <label style={labelStyle}>Competence Registration</label>
                            <input type="text" value={form.competenceRegistration} onChange={e => setForm({ ...form, competenceRegistration: e.target.value })} style={inputStyle} placeholder="e.g. Gas Safety" required />
                        </div>
                        <div>
                            <label style={labelStyle}>Registration Number</label>
                            <input type="text" value={form.registrationNumber} onChange={e => setForm({ ...form, registrationNumber: e.target.value })} style={inputStyle} placeholder="e.g. 503346" required />
                        </div>
                    </div>

                    {/* Insurance Section */}
                    <div className="mt-8 pt-8 border-t border-gray-100">
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Insurance & Liability</h3>
                        <p className="text-sm text-gray-400 mb-6">Upload or provide your certificate reference numbers</p>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                                <label style={labelStyle}>Trade Insurance Cert.</label>
                                <input type="text" value={form.insuranceCertificate} onChange={e => setForm({ ...form, insuranceCertificate: e.target.value })} style={inputStyle} placeholder="Certificate ref." />
                            </div>
                            <div>
                                <label style={labelStyle}>Public Liability</label>
                                <input type="text" value={form.publicLiability} onChange={e => setForm({ ...form, publicLiability: e.target.value })} style={inputStyle} placeholder="Policy number" />
                            </div>
                            <div>
                                <label style={labelStyle}>Professional Indemnity</label>
                                <input type="text" value={form.professionalIndemnity} onChange={e => setForm({ ...form, professionalIndemnity: e.target.value })} style={inputStyle} placeholder="Policy number" />
                            </div>
                        </div>
                    </div>

                    {/* Policy Notice */}
                    <div className="mt-8 bg-amber-50 border border-amber-200 rounded-2xl p-5">
                        <h4 className="font-bold text-amber-900 text-sm mb-2 flex items-center gap-2">
                            <i className="fas fa-exclamation-triangle text-amber-600"></i>
                            Partner Code of Conduct
                        </h4>
                        <ul className="text-sm text-amber-800 space-y-1.5">
                            <li>🟡 <strong>Yellow Card:</strong> Issued for service quality issues or missed appointments. <strong>2 yellow cards = account terminated.</strong></li>
                            <li>🔴 <strong>Red Card:</strong> Immediate removal from the network if a customer is disappointed after you accept a job on the portal.</li>
                            <li>📧 Jobs will be dispatched from <strong>partners@compliance-engine.io</strong></li>
                        </ul>
                    </div>

                    <div className="mt-8">
                        <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.7 : 1 }}>
                            {loading ? 'Submitting...' : 'Complete Onboarding & Start Receiving Jobs'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ============================================
// SHARED HEADER
// ============================================
function PortalHeader({ partner, onLogout }: { partner?: any; onLogout?: () => void }) {
    return (
        <div className="bg-white border-b border-gray-100 px-8 py-4 flex justify-between items-center sticky top-0 z-50">
            <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded bg-emerald-600 shadow-sm">
                    <i className="fas fa-tools text-white text-sm"></i>
                </div>
                <span className="font-bold text-lg text-gray-900 tracking-tight">
                    {BRAND.name} <span className="text-gray-400 font-normal mx-1">|</span> <span className="text-gray-600 font-semibold">{BRAND.sub}</span>
                </span>
            </div>
            <div className="flex items-center gap-6">
                {partner && (
                    <div className="text-right hidden sm:block">
                        <div className="text-sm font-bold text-gray-900">{partner.businessName}</div>
                        <div className="text-xs font-bold uppercase tracking-wider" style={{ color: partner.verificationStatus === 'verified' ? BRAND.emerald : '#d97706' }}>
                            {partner.onboardingComplete ? 'Active' : partner.verificationStatus}
                        </div>
                    </div>
                )}
                {onLogout && (
                    <button onClick={onLogout} className="text-xs px-4 py-2 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50 font-bold transition-all">
                        Sign Out
                    </button>
                )}
            </div>
        </div>
    );
}

// ============================================
// MAIN COMPONENT
// ============================================
function ResetPasswordForm({ email, onSuccess, onBack }: { email: string; onSuccess: () => void; onBack: () => void }) {
    const [stage, setStage] = useState<'request' | 'confirm'>('request');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await landlordApi.post('api/partners/forgot-password', { email });
            setStage('confirm');
            setSuccess('Reset code sent to your email.');
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to send reset code.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        setLoading(true);
        try {
            await landlordApi.post('api/partners/reset-password', {
                email,
                code,
                newPassword
            });
            setSuccess('Password reset successful! Redirecting to login...');
            setTimeout(onSuccess, 2000);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to reset password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: 400, margin: '0 auto', background: '#fff', padding: 40, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 8, textAlign: 'center' }}>
                {stage === 'request' ? 'Reset Password' : 'Set New Password'}
            </h2>
            <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginBottom: 32 }}>
                {stage === 'request' ? 'Enter your email to receive a reset code' : 'Enter the code from your email'}
            </p>

            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 12, marginBottom: 20, fontSize: 14, fontWeight: 600 }}>{error}</div>}
            {success && <div style={{ background: '#dcfce7', color: '#166534', padding: '12px 16px', borderRadius: 12, marginBottom: 20, fontSize: 14, fontWeight: 600 }}>{success}</div>}

            {stage === 'request' ? (
                <form onSubmit={handleRequest}>
                    <div style={{ marginBottom: 24 }}>
                        <label style={labelStyle}>Email Address</label>
                        <input
                            type="email"
                            required
                            value={email}
                            disabled
                            style={{ ...inputStyle, backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                            placeholder="partner@firm.com"
                        />
                    </div>

                    <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.7 : 1, marginBottom: 16 }}>
                        {loading ? 'Sending...' : 'Send Reset Code'}
                    </button>

                    <button
                        type="button"
                        onClick={onBack}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: BRAND.emerald,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            width: '100%',
                            padding: '12px 0',
                            textDecoration: 'underline'
                        }}
                    >
                        Back to Login
                    </button>
                </form>
            ) : (
                <form onSubmit={handleConfirm}>
                    <div style={{ marginBottom: 20 }}>
                        <label style={labelStyle}>Reset Code (from email)</label>
                        <input
                            type="text"
                            required
                            value={code}
                            onChange={e => setCode(e.target.value)}
                            style={inputStyle}
                            placeholder="123456"
                        />
                    </div>

                    <div style={{ marginBottom: 24 }}>
                        <label style={labelStyle}>New Password</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPassword ? "text" : "password"}
                                required
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                style={inputStyle}
                                placeholder="••••••••"
                                minLength={8}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: 12,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    fontSize: 16,
                                    padding: 4,
                                }}
                                title={showPassword ? 'Hide password' : 'Show password'}
                            >
                                <i className={`fas fa-eye${showPassword ? '' : '-slash'}`}></i>
                            </button>
                        </div>
                    </div>

                    <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.7 : 1, marginBottom: 16 }}>
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>

                    <button
                        type="button"
                        onClick={() => setStage('request')}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: BRAND.emerald,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            width: '100%',
                            padding: '12px 0',
                            textDecoration: 'underline'
                        }}
                    >
                        Back
                    </button>
                </form>
            )}
        </div>
    );
}

function ForgotPasswordForm({ onBack, onSuccess }: { onBack: () => void; onSuccess: () => void }) {
    const [stage, setStage] = useState<'request' | 'confirm'>('request');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const handleRequest = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            await landlordApi.post('api/partners/forgot-password', { email });
            setStage('confirm');
            setSuccess('Reset code sent to your email.');
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to send reset code.');
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        setLoading(true);
        try {
            await landlordApi.post('api/partners/reset-password', {
                email,
                code,
                newPassword
            });
            setSuccess('Password reset successful! Redirecting to login...');
            setTimeout(onSuccess, 2000);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to reset password.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ maxWidth: 400, margin: '0 auto', background: '#fff', padding: 40, borderRadius: 24, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.1)' }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 8, textAlign: 'center' }}>
                {stage === 'request' ? 'Reset Password' : 'Set New Password'}
            </h2>
            <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 14, marginBottom: 32 }}>
                {stage === 'request' ? 'Enter your email to receive a reset code' : 'Enter the code from your email'}
            </p>

            {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 12, marginBottom: 20, fontSize: 14, fontWeight: 600 }}>{error}</div>}
            {success && <div style={{ background: '#dcfce7', color: '#166534', padding: '12px 16px', borderRadius: 12, marginBottom: 20, fontSize: 14, fontWeight: 600 }}>{success}</div>}

            {stage === 'request' ? (
                <form onSubmit={handleRequest}>
                    <div style={{ marginBottom: 24 }}>
                        <label style={labelStyle}>Email Address</label>
                        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} placeholder="partner@firm.com" />
                    </div>

                    <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.7 : 1, marginBottom: 16 }}>
                        {loading ? 'Sending...' : 'Send Reset Code'}
                    </button>

                    <button
                        type="button"
                        onClick={onBack}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: BRAND.emerald,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            width: '100%',
                            padding: '12px 0',
                            textDecoration: 'underline'
                        }}
                    >
                        Back to Login
                    </button>
                </form>
            ) : (
                <form onSubmit={handleConfirm}>
                    <div style={{ marginBottom: 20 }}>
                        <label style={labelStyle}>Reset Code (from email)</label>
                        <input type="text" required value={code} onChange={e => setCode(e.target.value)} style={inputStyle} placeholder="123456" />
                    </div>

                    <div style={{ marginBottom: 24 }}>
                        <label style={labelStyle}>New Password</label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPassword ? "text" : "password"}
                                required
                                value={newPassword}
                                onChange={e => setNewPassword(e.target.value)}
                                style={inputStyle}
                                placeholder="••••••••"
                                minLength={8}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                style={{
                                    position: 'absolute',
                                    right: 12,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#6b7280',
                                    fontSize: 16,
                                    padding: 4,
                                }}
                                title={showPassword ? 'Hide password' : 'Show password'}
                            >
                                <i className={`fas fa-eye${showPassword ? '' : '-slash'}`}></i>
                            </button>
                        </div>
                    </div>

                    <button type="submit" disabled={loading} style={{ ...btnPrimary, opacity: loading ? 0.7 : 1, marginBottom: 16 }}>
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>

                    <button
                        type="button"
                        onClick={() => setStage('request')}
                        style={{
                            background: 'none',
                            border: 'none',
                            color: BRAND.emerald,
                            fontSize: 14,
                            fontWeight: 600,
                            cursor: 'pointer',
                            width: '100%',
                            padding: '12px 0',
                            textDecoration: 'underline'
                        }}
                    >
                        Back
                    </button>
                </form>
            )}
        </div>
    );
}

export default function PartnersLanding() {
    const [view, setView] = useState<'landing' | 'login' | 'register' | 'forgot-password' | 'dashboard'>('landing');
    const [token, setToken] = useState<string | null>(null);
    const [partner, setPartner] = useState<any>(null);
    const [profileLoading, setProfileLoading] = useState(false);
    const [resetEmail, setResetEmail] = useState<string | null>(null);

    // No need for URL token - use code-based reset like Cognito

    // Fetch profile from backend (works for both login and token restore)
    const fetchProfile = async () => {
        setProfileLoading(true);
        try {
            const res = await partnerService.getProfile();
            setPartner(res.data.partner);
        } catch (err: any) {
            console.error('[Partners] Profile fetch failed:', err);
            // Token is invalid or expired — force re-login
            localStorage.removeItem('tradePartnerToken');
            setToken(null);
            setPartner(null);
            setView('login');
        } finally {
            setProfileLoading(false);
        }
    };

    // Auto-login check — fetch profile from backend
    useEffect(() => {
        const saved = localStorage.getItem('tradePartnerToken');
        if (saved) {
            setToken(saved);
            setView('dashboard');
            // Always fetch fresh profile from backend
            fetchProfile();
        }
        // eslint-disable-next-line
    }, []);

    const handleLogin = (t: string, p: any) => {
        setToken(t);
        setPartner(p);
        setView('dashboard');
        // Also fetch fresh profile (login response has partner, but let's ensure it's fresh)
        fetchProfile();
    };

    const logout = () => {
        localStorage.removeItem('tradePartnerToken');
        setToken(null);
        setPartner(null);
        setView('landing');
    };

    // ============================================
    // LANDING PAGE
    // ============================================
    if (view === 'landing') {
        return (
            <div className="bg-white font-sans text-gray-900 min-h-screen flex flex-col relative overflow-hidden">
                {/* DYNAMIC BACKGROUND IMAGE */}
                <div
                    className="absolute inset-0 z-0 opacity-[0.25] pointer-events-none"
                    style={{
                        backgroundImage: `url("/manchester_city_council/trade_partners.png")`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        filter: 'grayscale(100%) brightness(0.95)'
                    }}
                />

                {/* NAV */}
                <nav className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
                    <div className="max-w-7xl mx-auto px-6 flex justify-between items-center h-16">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded bg-emerald-600 shadow-sm">
                                <i className="fas fa-tools text-white text-sm"></i>
                            </div>
                            <span className="font-bold text-lg text-gray-900 tracking-tight">{BRAND.name} <span className="text-gray-400 font-normal mx-1">|</span> <span className="text-gray-600 font-semibold">Trade Partners</span></span>
                        </div>
                        <div className="flex items-center gap-4">
                            <button onClick={() => setView('login')} className="text-gray-600 hover:text-emerald-600 font-semibold transition-colors">
                                Partner Login
                            </button>
                            <button onClick={() => setView('register')} className="bg-emerald-600 text-white px-5 py-2 rounded-lg font-bold hover:bg-emerald-700 transition shadow-sm">
                                Join Trade Network
                            </button>
                        </div>
                    </div>
                </nav>

                {/* HERO */}
                <section className="relative z-10 py-24 md:py-36 flex-1 flex flex-col justify-center">
                    <div className="max-w-5xl mx-auto px-6 text-center">
                        <div className="inline-block bg-emerald-100/60 text-emerald-800 px-4 py-1.5 rounded-full text-sm font-bold mb-8 backdrop-blur-sm border border-emerald-200/50">
                            Steady Work · Fast Payouts
                        </div>
                        <h1 className="text-5xl md:text-8xl font-black mb-8 text-gray-900 leading-[0.95] tracking-tighter">
                            A Constant Stream of <span className="text-emerald-600">Local Jobs.</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed max-w-3xl mx-auto font-medium">
                            Stop chasing leads and quoting for free. Get directly booked for regular, steady, and ongoing compliance work from the UK's largest professional landlord portfolios. We handle the paperwork, you do the trade.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <button onClick={() => setView('register')} className="bg-emerald-600 text-white px-10 py-5 rounded-2xl font-bold hover:bg-emerald-700 transition shadow-2xl shadow-emerald-600/20 text-lg flex items-center justify-center gap-3">
                                Start Receiving Jobs <i className="fas fa-hammer"></i>
                            </button>
                            <button onClick={() => setView('login')} className="bg-white/80 backdrop-blur-sm border border-gray-200 text-gray-700 px-10 py-5 rounded-2xl font-bold hover:bg-gray-50 transition text-lg">
                                Partner Hub Login
                            </button>
                        </div>
                    </div>
                </section>

                {/* Integration Bar */}
                <section className="relative z-10 py-12 border-t border-gray-100 bg-white/40 backdrop-blur-sm">
                    <div className="max-w-7xl mx-auto px-6">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 text-center">
                            <div>
                                <h3 className="font-bold text-gray-900 mb-1">Ongoing Volume</h3>
                                <p className="text-sm text-gray-500">Steady job queue daily</p>
                            </div>
                            <div className="md:border-x border-gray-100">
                                <h3 className="font-bold text-gray-900 mb-1">Guaranteed Payout</h3>
                                <p className="text-sm text-gray-500">Automated ledger payments</p>
                            </div>
                            <div className="md:border-r border-gray-100">
                                <h3 className="font-bold text-gray-900 mb-1">Zero Bidding</h3>
                                <p className="text-sm text-gray-500">Jobs assigned directly</p>
                            </div>
                            <div>
                                <h3 className="font-bold text-gray-900 mb-1">Smart Scheduling</h3>
                                <p className="text-sm text-gray-500">Work in your preferred areas</p>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        );
    }

    // ============================================
    // AUTHENTICATION PAGES
    // ============================================
    if (view === 'register') {
        return (
            <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
                <div style={{ padding: '20px 32px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button onClick={() => setView('landing')} className="text-emerald-600 font-bold hover:underline">← Back to Portal</button>
                    <button onClick={() => setView('login')} className="text-gray-500 text-sm font-semibold">Already approved? Login</button>
                </div>
                <div style={{ padding: '64px 24px' }}>
                    <PartnerRegistration onRegistered={(email) => setView('login')} />
                </div>
            </div>
        );
    }

    if (view === 'login') {
        return (
            <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
                <div style={{ padding: '20px 32px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button onClick={() => setView('landing')} className="text-emerald-600 font-bold hover:underline">← Back to Portal</button>
                    <button onClick={() => setView('register')} className="text-gray-500 text-sm font-semibold">New firm? Apply to join</button>
                </div>
                <div style={{ padding: '64px 24px' }}>
                    <PartnerLogin
                        onLogin={handleLogin}
                        onForgotPassword={() => setView('forgot-password')}
                    />
                </div>
            </div>
        );
    }

    if (view === 'forgot-password') {
        return (
            <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
                <div style={{ padding: '20px 32px', background: '#fff', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <button onClick={() => setView('landing')} className="text-emerald-600 font-bold hover:underline">← Back to Portal</button>
                    <button onClick={() => setView('login')} className="text-gray-500 text-sm font-semibold">Back to Login</button>
                </div>
                <div style={{ padding: '64px 24px' }}>
                    <ForgotPasswordForm
                        onBack={() => setView('login')}
                        onSuccess={() => setView('login')}
                    />
                </div>
            </div>
        );
    }

    // ============================================
    // DASHBOARD (Status-Aware)
    // ============================================
    if (view === 'dashboard') {
        // Loading state
        if (profileLoading && !partner) {
            return (
                <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
                    <PortalHeader />
                    <div className="flex-1 flex items-center justify-center">
                        <div className="text-center">
                            <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-gray-500 font-medium">Loading your dashboard...</p>
                        </div>
                    </div>
                </div>
            );
        }

        const isVerified = partner?.verificationStatus === 'verified';
        const isOnboarded = partner?.onboardingComplete === true;

        return (
            <div className="min-h-screen bg-gray-50 flex flex-col font-sans">
                <PortalHeader partner={partner} onLogout={logout} />

                {/* STATE 1: Not yet verified — Under Review */}
                {!isVerified && (
                    <div className="flex-1 flex flex-col justify-center items-center p-8">
                        <div className="max-w-2xl w-full bg-white p-12 md:p-16 rounded-[40px] shadow-2xl shadow-gray-200/50 border border-gray-50 text-center relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-2 bg-amber-400"></div>

                            <div className="w-24 h-24 bg-amber-50 text-amber-500 rounded-full flex items-center justify-center text-4xl mx-auto mb-10 shadow-inner">
                                <i className="fas fa-shield-alt"></i>
                            </div>

                            <h2 className="text-4xl font-black text-gray-900 mb-6 tracking-tight">Under Review</h2>
                            <p className="text-lg text-gray-500 leading-relaxed mb-10 max-w-md mx-auto">
                                Your trade account is currently in the verification queue. We're cross-referencing your credentials with national safety registers today.
                            </p>

                            <div className="inline-flex items-center gap-3 bg-amber-50 text-amber-700 px-6 py-3 rounded-2xl text-sm font-black uppercase tracking-widest border border-amber-100">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                                </span>
                                Identity & Certification Audit
                            </div>
                        </div>
                    </div>
                )}

                {/* STATE 2: Verified but NOT onboarded — Show onboarding form */}
                {isVerified && !isOnboarded && (
                    <OnboardingForm partner={partner} onComplete={(updated) => setPartner(updated)} />
                )}

                {/* STATE 3: Verified AND onboarded — Full dashboard */}
                {isVerified && isOnboarded && (
                    partner?.role === 'partner_admin' ? (
                        <PartnerAdminCalendar />
                    ) : (
                        <PartnerJobQueue partner={partner} />
                    )
                )}
            </div>
        );
    }

    return null;
}

// ============================================
// STYLES
// ============================================
const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '14px 18px',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 500,
    outline: 'none',
    boxSizing: 'border-box',
    background: '#fff',
    color: '#0f172a',
    transition: 'border-color 0.2s'
};

const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: 13,
    fontWeight: 700,
    color: '#4b5563',
    marginBottom: 8,
    textTransform: 'uppercase',
    letterSpacing: '0.025em'
};

const btnPrimary: React.CSSProperties = {
    background: '#059669',
    color: '#fff',
    border: 'none',
    borderRadius: 14,
    padding: '14px 28px',
    fontSize: 16,
    fontWeight: 800,
    cursor: 'pointer',
    width: '100%',
    transition: 'all 0.2s',
    boxShadow: '0 10px 15px -3px rgba(5, 150, 105, 0.2)'
};
